import { gql, QueryHookOptions, useQuery } from '@apollo/client'

import { PermissionChoices } from 'src/gen-types'

import { GetMeRolePermissions } from './__generated__/types'

const GET_ME_ROLE_PERMISSIONS = gql`
  query GetMeRolePermissions {
    me {
      id
      isSuperuser
      isStaff
      isMaster
      permissions
    }
  }
`

/**
 * Reads the current user's role permission codes — the combined permissions
 * of every role they hold (PRO-945) — and the superuser-style flags that
 * bypass them. Runs cache-and-network: the cached GET_ME entry
 * resolves the first render synchronously (no loading state), while a
 * background refetch picks up permission changes made during the session —
 * without it, cache-first would serve stale perms until a full page reload.
 */
export const useUserRolePermissions = (
  options?: QueryHookOptions<GetMeRolePermissions>
) => {
  const { data, ...rest } = useQuery<GetMeRolePermissions>(
    GET_ME_ROLE_PERMISSIONS,
    { fetchPolicy: 'cache-and-network', ...options }
  )

  const me = data?.me
  const permissions: PermissionChoices[] = me?.permissions ?? []
  // Superusers / staff / master users implicitly hold every permission.
  const bypass = Boolean(me?.isSuperuser || me?.isStaff || me?.isMaster)

  return {
    ...rest,
    permissions,
    isSuperuser: Boolean(me?.isSuperuser),
    has: (code: PermissionChoices) => bypass || permissions.includes(code),
  }
}
